<template>
  <app-field
    v-slot="{ describedBy: fieldDescribedBy, invalid }"
    :label="label"
    :label-for="inputId"
    :help="help"
    :hint="hint"
    :tooltip="tooltip"
    :error="fieldError"
    :success="success"
    :required="required"
    :described-by="mergeDescribedBy(describedBy, attrs['aria-describedby'])"
    :label-class="labelClass"
    :container-class="containerClass"
  >
    <div class="relative w-full" :class="wrapperClass">
      <span
        v-if="$slots.prefix || prefixIcon"
        class="pointer-events-none absolute inset-y-0 z-10 flex items-center text-theme-text-muted"
        :style="prefixStyle"
      >
        <slot name="prefix">
          <Icon :path="prefixIcon" :class="tokens.icon" />
        </slot>
      </span>

      <select
        v-bind="attrs"
        :id="inputId"
        ref="inputEl"
        :name="name"
        v-model="computedValue"
        :disabled="disabled"
        :required="required"
        :autocomplete="autocomplete"
        :aria-describedby="fieldDescribedBy"
        :aria-invalid="invalid || attrs['aria-invalid']"
        :class="controlClass"
        :style="controlStyle"
        @change="handleChange"
        @focus="handleFocus"
        @blur="handleBlur"
        @invalid="handleInvalid"
      >
        <option v-if="placeholder" :value="emptyValue">
          {{ placeholder }}
        </option>
        <!-- Options carrying a `group` render under an <optgroup> of that label. -->
        <template v-for="(group, groupIndex) in optionGroups" :key="group.label || groupIndex">
          <optgroup v-if="group.label" :label="group.label">
            <option v-for="option in group.options" :key="option.key" :value="option.value" :disabled="option.disabled" :title="option.title">
              {{ option.label }}
            </option>
          </optgroup>
          <template v-else>
            <option v-for="option in group.options" :key="option.key" :value="option.value" :disabled="option.disabled" :title="option.title">
              {{ option.label }}
            </option>
          </template>
        </template>
      </select>

      <span
        class="pointer-events-none absolute inset-y-0 z-10 flex items-center text-theme-text-muted"
        :style="chevronStyle"
      >
        <Icon :path="mdiChevronDown" :class="tokens.icon" />
      </span>
    </div>
  </app-field>
</template>

<script>
import { computed, ref, useAttrs, watchEffect } from 'vue'
import { mdiChevronDown } from '@mdi/js'

import AppField from '@/components/ui/forms/AppField.vue'
import Icon from '@/components/Icon.vue'
import { hasValidationError } from '@/composables/useValidationState'
import {
  getFormControlId,
  mergeDescribedBy,
  resolveOptionLabel,
  resolveOptionValue,
  useFormControlStyles
} from '@/composables/useFormControlStyles'

export default {
  name: 'AppSelect',
  components: {
    AppField,
    Icon
  },
  inheritAttrs: false,
  props: {
    modelValue: { type: [String, Number, Boolean, Object], default: '' },
    label: { type: String, default: '' },
    help: { type: String, default: '' },
    hint: { type: String, default: '' },
    tooltip: { type: String, default: '' },
    error: { type: [String, Boolean, Array], default: false },
    validationMessage: { type: String, default: '' },
    success: { type: [String, Boolean], default: false },
    size: { type: String, default: 'md' },
    variant: { type: String, default: 'default' },
    disabled: Boolean,
    required: Boolean,
    name: { type: String, default: '' },
    id: { type: String, default: '' },
    autocomplete: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    optionLabel: { type: String, default: '' },
    optionValue: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    emptyLabel: { type: String, default: '' },
    prefixIcon: { type: String, default: '' },
    inputClass: { type: [String, Array, Object], default: '' },
    wrapperClass: { type: [String, Array, Object], default: '' },
    labelClass: { type: [String, Array, Object], default: '' },
    containerClass: { type: [String, Array, Object], default: '' },
    describedBy: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'change', 'focus', 'blur'],
  setup (props, { emit, expose }) {
    const attrs = useAttrs()
    const inputEl = ref(null)
    const showValidation = ref(false)
    const nativeError = ref('')
    const fieldError = computed(() => hasValidationError(props.error) ? props.error : (showValidation.value ? props.validationMessage || nativeError.value || false : false))
    watchEffect(() => inputEl.value?.setCustomValidity(props.validationMessage))
    const generatedId = getFormControlId('app-select')
    const { tokens, inputBaseClass } = useFormControlStyles(props, fieldError)

    const inputId = computed(() => props.id || generatedId)
    const hasPrefix = computed(() => Boolean(props.prefixIcon))
    const emptyValue = computed(() => props.emptyLabel || '')
    const sizeKey = computed(() => (props.size && ['xs', 'sm', 'md', 'lg'].includes(props.size) ? props.size : 'md'))
    const horizontalInset = computed(() => `var(--space-control-x-${sizeKey.value})`)
    const iconPad = computed(() => {
      switch (sizeKey.value) {
        case 'xs':
          return '1.5rem'
        case 'sm':
          return '2rem'
        case 'lg':
          return '2rem'
        default:
          return '1.875rem'
      }
    })
    const computedValue = computed({
      get: () => props.modelValue,
      set: (value) => {
        emit('update:modelValue', value)
      }
    })

    const normalizedOptions = computed(() =>
      props.options.map((option, index) => ({
        key: option?.id ?? `${index}-${String(resolveOptionLabel(option, props.optionLabel))}`,
        value: resolveOptionValue(option, props.optionValue),
        label: resolveOptionLabel(option, props.optionLabel),
        disabled: !!option?.disabled,
        // Optional tooltip, e.g. why an option is disabled.
        title: option?.title || undefined,
        group: option?.group ?? ''
      }))
    )

    // Consecutive options of one `group` share an <optgroup>; ungrouped ones stay bare.
    const optionGroups = computed(() =>
      normalizedOptions.value.reduce((groups, option) => {
        const last = groups[groups.length - 1]
        if (last && last.label === option.group) last.options.push(option)
        else groups.push({ label: option.group, options: [option] })
        return groups
      }, [])
    )

    const controlClass = computed(() => {
      const classes = [...inputBaseClass.value, 'app-select-theme', 'appearance-none']

      if (props.inputClass) {
        classes.push(props.inputClass)
      }

      return classes
    })

    const controlStyle = computed(() => ({
      paddingLeft: hasPrefix.value
        ? `calc(${horizontalInset.value} + ${iconPad.value})`
        : horizontalInset.value,
      paddingRight: `calc(${horizontalInset.value} + ${iconPad.value})`,
      // Native selects already centre their text within the fixed control height.
      // Extra block padding can exceed that height at `sm` and clip descenders.
      paddingTop: '0',
      paddingBottom: '0'
    }))

    const prefixStyle = computed(() => ({
      left: horizontalInset.value
    }))

    const chevronStyle = computed(() => ({
      right: horizontalInset.value,
      left: 'auto'
    }))

    function handleChange (event) {
      nativeError.value = event.target.validity.customError ? '' : event.target.validationMessage
      emit('change', event)
    }

    function handleInvalid (event) {
      showValidation.value = true
      nativeError.value = event.target.validity.customError ? '' : event.target.validationMessage
    }

    function handleFocus (event) {
      emit('focus', event)
    }

    function handleBlur (event) {
      emit('blur', event)
    }

    expose({
      inputEl,
      focus: () => inputEl.value?.focus(),
      blur: () => inputEl.value?.blur()
    })

    return {
      attrs,
      mergeDescribedBy,
      mdiChevronDown,
      tokens,
      inputEl,
      inputId,
      showValidation,
      fieldError,
      emptyValue,
      computedValue,
      normalizedOptions,
      optionGroups,
      controlClass,
      controlStyle,
      prefixStyle,
      chevronStyle,
      handleChange,
      handleInvalid,
      handleFocus,
      handleBlur
    }
  }
}
</script>

<style scoped>
select.app-select-theme {
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  background-image: none !important;
  background-position: initial !important;
  background-repeat: no-repeat !important;
}

select.app-select-theme::-ms-expand {
  display: none;
}
</style>
